<template>
  <CContainer v-if="picaService">
    <h1 class="main-header">
      <template v-if="isEditMode">
        <translate>Event setup</translate>
      </template>

      <template v-else>
        <translate>New event</translate>
      </template>
    </h1>

    <CCard v-if="isEditMode">
      <CCardBody class="px-0">
        <CContainer fluid class="grid-stripped">
          <CRow v-if="isFieldVisible('picaService.event.is_active')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Active</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitchConfirm v-if="isFieldEditable('picaService.event.is_active')"
                              class="mx-1" color="primary" variant="3d" size="sm"
                              :modal-title="$gettext('Confirm state change')"
                              :checked="isActive" @click-confirm="saveActivationStatus">
                <template #modal-body-status-will-true>
                  <translate>You are about to make the event active! Are you sure?</translate>
                </template>
                <template #modal-body-status-will-false>
                  <translate>You are about to deactivate the event! Are you sure?</translate>
                </template>
              </CSwitchConfirm>

              <div v-else>{{ picaService.event.is_active }}</div>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Tag status</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <p v-if="picaService.is_tagging_completed" class="m-0">
                            <span class="text-success text-nowrap d-block">
                                <translate>completed</translate>
                            </span>
                <translate>on</translate>
                {{ picaService.tagging_completed_at|formatDateTime }}<br>
                <template v-if="picaService.tagging_completed_by">
                  <translate>by</translate>
                  {{ picaService.tagging_completed_by.email }}
                </template>
              </p>
              <div class="d-flex flex-row align-items-center" v-else>
              <span class="text-warning text-nowrap mr-4">
                  <translate>in progress</translate>
              </span>

                <ButtonCompleteTag
                    v-if="isFieldEditable('picaService.is_tagging_completed') && hasPerm('pica.set_tagging_completed')"
                    class="mt-2"
                    @tag-completed="refreshPicaServiceInStore"
                />

              </div>
            </CCol>
          </CRow>
        </CContainer>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardHeader>
        <h5 class="my-1" v-translate>Pica Service</h5>
        <div class="card-header-actions">
          <PButton :loading="loading" class="btn btn-primary btn-sm ml-2" @click="save">
            <template v-if="isEditMode">
              <translate>Save changes</translate>
            </template>
            <template v-else>
              <translate>Create event</translate>
            </template>
          </PButton>
        </div>
      </CCardHeader>

      <CCardBody class="px-0">
        <CContainer fluid class="grid-stripped">
          <CRow v-if="isFieldVisible('picaService.event_owner')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Event owner</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.event_owner')">
              <PSelect
                  :loading="eventOwnersLoading"
                  :options="eventOwners"
                  v-model="picaService.event_owner"
                  track-by="id"
                  :placeholder="$gettext('Select an event owner... (empty for Pica events)')"
                  :custom-label="(option) => option.company_name"
                  :select-label="$gettext('select')"
                  :can-remove-selected="true"
                  :close-on-select="true"
                  :fetch-data-function="fetchEventOwners"
                  :clear-data-function="clearEventOwners"
                  :nextPage="eventOwnersNextPage"
              >
              </PSelect>
              <div class="invalid-feedback" style="display: block"
                   v-if="isFieldValid('event.event_owner_id') === false">
                {{ getFieldErrors('event.event_owner_id') }}
              </div>
            </CCol>
            <CCol sm="7" lg="4" v-else>
              <template v-if="picaService.event_owner">
                {{ picaService.event_owner.company_name }}
              </template>
              <template v-else>-</template>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Name</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CInput v-if="isFieldEditable('picaService.event.name')"
                      v-model="picaService.event.name"
                      required
                      :is-valid="isFieldValid('event.name')"
                      :invalid-feedback="getFieldErrors('event.name')"
              />
              <p v-else>{{ picaService.event.name }}</p>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Subheading</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <!--                        <PInputTextMultilang />-->
              <CInput v-if="isFieldEditable('picaService.event.photo_service')"
                      v-model="picaService.event.photo_service"/>
              <p v-else-if="picaService.event.photo_service">{{ picaService.event.photo_service }}</p>
              <p v-else>-</p>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Starts on</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.event.start_at')">
              <VueCtkDateTimePicker
                  id="event-start-at"
                  v-model="picaService.event.start_at"
                  @input="checkDateTimeValue($event)"
                  :no-label="true"
                  color="#1867FF"
                  :label="$gettext('Select date and time')"
                  format="YYYY-MM-DD HH:mm"
                  formatted="LLLL"
                  :locale="language"
                  :no-button-now="true"
              />
              <div class="invalid-feedback" style="display: block"
                   v-if="isFieldValid('event.start_at') === false">
                {{ getFieldErrors('event.start_at') }}
              </div>
            </CCol>

            <CCol sm="7" lg="4" v-else>
              {{ picaService.event.start_at|formatDate }}
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Ends on</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.event.end_at')">
              <VueCtkDateTimePicker
                  id="event-end-at"
                  v-model="picaService.event.end_at"
                  :no-label="true"
                  color="#1867FF"
                  :label="$gettext('Select date and time')"
                  format="YYYY-MM-DD HH:mm"
                  formatted="LLLL"
                  :locale="language"
                  :no-button-now="true"
              />
              <div class="invalid-feedback" style="display: block"
                   v-if="isFieldValid('event.end_at') === false">
                {{ getFieldErrors('event.end_at') }}
              </div>
            </CCol>

            <CCol sm="7" lg="4" v-else>
              {{ picaService.event.end_at|formatDate }}
            </CCol>
          </CRow>
          <CRow v-if="isFieldVisible('picaService.event.category')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Category</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.event.category')">
              <multiselect
                  :options="eventCategories"
                  v-model="picaService.event.category"
                  track-by="id"
                  label="name"
                  :placeholder="$gettext('Select a category...')"
                  :allow-empty="false"
              />
              <div class="invalid-feedback" style="display: block"
                   v-if="isFieldValid('event.category_id') === false">
                {{ getFieldErrors('event.category_id') }}
              </div>
            </CCol>
            <CCol sm="7" lg="4" v-else>
              {{ picaService.event.category.name }}
            </CCol>
          </CRow>
          <CRow v-if="isFieldVisible('picaService.codec')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Codec</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <multiselect
                  :options="codecs"
                  :value="picaService.codec"
                  @input="handleCodecChange"
                  track-by="id"
                  label="label"
                  placeholder="Scegli il codec..."
                  :allow-empty="false"
              />
              <div class="invalid-feedback" style="display: block" v-if="isFieldValid('codec_id') === false">
                {{ getFieldErrors('codec_id') }}
              </div>
            </CCol>
          </CRow>
        </CContainer>

      </CCardBody>

      <CCardFooter class="p-0">
        <CContainer fluid class="grid-stripped">
          <CRow v-if="isSelectedFaceRecognitionCodec && isFieldVisible('picaService.allows_upload_from_mobile')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Allow photo upload from smartphones and dashboards</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.allows_upload_from_mobile"/>
            </CCol>
          </CRow>

          <CRow v-if="isFieldVisible('picaService.allows_upload_from_video_extraction')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Allow photo upload from video extraction</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm"
                       :checked.sync="picaService.allows_upload_from_video_extraction"/>
            </CCol>
          </CRow>

          <CRow v-if="! isSelectedFaceRecognitionCodec && isFieldVisible('picaService.allows_upload_video')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Allow video uploads</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.allows_upload_video"/>
            </CCol>
          </CRow>

          <CRow v-if="isFieldVisible('picaService.has_media_original')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Allow HD photo uploads</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <!--                        labelOn="Sì" labelOff="No" -->
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.has_media_original"/>
            </CCol>
          </CRow>

          <CRow v-if="picaService.has_media_original">
            <CCol sm="5" lg="3">
              <label>
                <translate>Delivery HD Photos</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <VueCtkDateTimePicker
                  v-if="isFieldEditable('picaService.hd_available_from')"
                  id="hd-available-from"
                  v-model="picaService.hd_available_from"
                  :no-label="true"
                  color="#1867FF"
                  :label="$gettext('Select date and time')"
                  format="YYYY-MM-DD HH:mm"
                  formatted="LLLL"
                  :locale="language"
                  :no-button-now="true"/>
              <div v-else-if="$store.state.event.picaService.hd_available_from">
                {{ picaService.hd_available_from|formatDate }}
              </div>
              <div v-else v-translate>Date not set</div>
            </CCol>
          </CRow>
        </CContainer>

      </CCardFooter>

      <CCardFooter class="p-0" v-if="isFieldVisible('picaService.is_magictag_enabled')">
        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol><h6>Tagging</h6></CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Enable the MagicTag</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.is_magictag_enabled"/>
            </CCol>
          </CRow>

          <CRow v-if="isFieldVisible('picaService.recognition_class')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Recognition class</translate>
              </label>
              <span class="ml-2" style="cursor: pointer" v-c-popover="{
                                    header: $gettext('Recognition class'),
                                    content: $gettext('It defines the elements that the AI system must recognize'),
                                    placement: 'top'
                                }">(?)</span>
            </CCol>
            <CCol sm="7" lg="4">
              <multiselect
                  :options="recognitionClasses"
                  v-model="selectedRecognitionClass"
                  track-by="code"
                  label="name"
                  placeholder="Scegli il recognition class..."
                  :allow-empty="false"
              />
              <div class="invalid-feedback" style="display: block"
                   v-if="isFieldValid('recognition_class') === false">
                {{ getFieldErrors('recognition_class') }}
              </div>
            </CCol>
          </CRow>

          <CRow v-if="isFieldVisible('picaService.face_match_min_confidence')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Face confidence</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4" class="d-flex">
              <input type="range" class="custom-range" min="0" max="1" step="0.01"
                     v-model="picaService.face_match_min_confidence">
              <label class="range-label">{{ displayFaceMatchMinConfidence }}</label>
            </CCol>
          </CRow>
        </CContainer>

      </CCardFooter>

      <CCardFooter class="p-0"
                   v-if="isFieldVisible('picaService.checkin_ask_face_validity') || isFieldVisible('picaService.checkin_max_faces_count')">
        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol><h6>Checkin</h6></CCol>
          </CRow>
          <CRow v-if="isFieldVisible('picaService.checkin_max_faces_count')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Allow multiple selfies</translate>
              </label>
              <span class="ml-2" style="cursor: pointer" v-c-popover="{
                                    trigger: 'hover',
                                    header: $gettext('Allow multiple selfies'),
                                    content: $gettext('It will allow the user to insert up to 10 selfies for his family members'),
                                    placement: 'top'
                                }">(?)</span>

            </CCol>
            <CCol sm="7" lg="4">
              <!--                        labelOn="Sì" labelOff="No" -->
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked="isCheckinMultipleFacesEnabled"
                       @update:checked="toggleCheckinMultipleFacesEnabled()"/>
            </CCol>
          </CRow>
          <CRow v-if="isFieldVisible('picaService.checkin_max_faces_count') && isCheckinMultipleFacesEnabled">
            <CCol sm="5" lg="3">
              <label>
                <translate>Maximum amount of selfies</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <multiselect
                  :options="maxFacesCount"
                  v-model="picaService.checkin_max_faces_count"
                  placeholder="Scegli il photo service..."
                  :allow-empty="false"
              />
            </CCol>
          </CRow>
          <CRow v-if="isFieldVisible('picaService.checkin_ask_face_validity')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Request date of end of stay</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.checkin_ask_face_validity_end"/>
            </CCol>
          </CRow>
          <CRow
              v-if="isFieldVisible('picaService.checkin_ask_face_validity') && picaService.checkin_ask_face_validity_end">
            <CCol sm="5" lg="3">
              <label>
                <translate>Request date of start of stay</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <!--                        labelOn="Sì" labelOff="No" -->
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm"
                       :checked.sync="picaService.checkin_ask_face_validity_start"/>
            </CCol>
          </CRow>
          <CRow v-if="isFieldVisible('picaService.checkin_anonymize_enabled')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Enable checkin anonymization</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.checkin_anonymize_enabled"/>
            </CCol>
          </CRow>
        </CContainer>

      </CCardFooter>

      <CCardFooter class="p-0"
                   v-if="isFieldVisible('picaService.print_service_enabled_v2') && picaService.has_media_original">
        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol>
              <h6>
                <translate>Print products</translate>
              </h6>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Enable printing with PhotoSì</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.print_service_enabled_v2"/>
            </CCol>
          </CRow>
          <CRow v-if="picaService.print_service_enabled_v2">
            <CCol sm="5" lg="3">
              <label>
                <translate>Print price list</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <multiselect
                  :options="printPriceLists"
                  v-model="picaService.print_pricelist"
                  track-by="id"
                  label="name"
                  placeholder="Scegli il listino prezzi..."
              />
            </CCol>
          </CRow>
        </CContainer>

      </CCardFooter>

      <CCardFooter class="p-0">
        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol>
              <h6>
                <translate>Photo Service</translate>
              </h6>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Agency name</translate>
              </label>
            </CCol>

            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.photo_service')">
              <multiselect
                  :options="photoServices"
                  v-model="picaService.photo_service"
                  track-by="id"
                  label="company_name"
                  placeholder="Scegli il photo service..."
                  :allow-empty="false"
              />
              <div class="invalid-feedback" style="display: block"
                   v-if="isFieldValid('photo_service_id') === false">
                {{ getFieldErrors('photo_service_id') }}
              </div>
            </CCol>

            <CCol sm="7" lg="4" v-else>
              {{ picaService.photo_service.company_name }}
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Photographer manager</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.photographer_manager')">
              <multiselect
                  :options="photoServicePhotographerManagers"
                  v-model="picaService.photographer_manager"
                  track-by="id"
                  placeholder="Scegli il photographer manager..."
              >
                <template slot="option" slot-scope="props">
                  {{ props.option.email }}
                  <!--                <small>Enrico Minotti {{ props.option.first_name }} {{ props.option.last_name }}</small>-->
                </template>

                <template slot="singleLabel" slot-scope="props">
                  {{ props.option.email }}
                  <!--                <small>Enrico Minotti {{ props.option.first_name }} {{ props.option.last_name }}</small>-->
                </template>
              </multiselect>
            </CCol>

            <CCol sm="7" lg="4" v-else>
              {{ picaService.photographer_manager.email }}
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Photographers</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <PMultiselectList
                  :options="photoServicePhotographers"
                  v-model="picaService.photographers"
                  track-by="id"
                  :placeholder="$gettext('Add a photographer...')"
                  :custom-label="(option) => option.email + ' ' +  option.first_name + ' ' +  option.last_name"
                  :select-label="$gettext('select')"
                  :can-remove-selected="true"
              >
                <template slot="multiselect-option" slot-scope="{ props }">
                  <span class="mb-0">{{ props.option.email }}</span>
                  <span class="text-muted" v-if="props.option.first_name || props.option.last_name">
                                    {{ props.option.first_name }} {{ props.option.last_name }}
                                </span>
                </template>

                <template #list-description="{ item }">
                  <div>
                    <span class="mb-0">{{ item.email }}</span>
                    <span class="text-muted" v-if="item.first_name || item.last_name">
                                        {{ item.first_name }} {{ item.last_name }}
                                    </span>
                  </div>
                </template>

                <template #no-options>
                  <translate>All photographers has been selected</translate>
                  <!--                                Hai selezionato tutti i fotografi-->
                </template>
              </PMultiselectList>
            </CCol>
          </CRow>
        </CContainer>
      </CCardFooter>

      <CCardFooter class="p-0"
                   v-if="isFieldVisible('picaService.printing_service') && ! isSelectedFaceRecognitionCodec">

        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol>
              <h6>
                <translate>Printing Service</translate>
              </h6>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Agency name</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <multiselect
                  :options="printingServices"
                  v-model="picaService.printing_service"
                  track-by="id"
                  label="company_name"
                  :placeholder="$gettext('Select a printing service...')"
              />
            </CCol>
          </CRow>
        </CContainer>
      </CCardFooter>

      <CCardFooter class="p-0" v-if="isFieldVisible('picaService.timing_service')">
        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol>
              <h6>
                <translate>Timing Service</translate>
              </h6>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Agency name</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <multiselect
                  :options="timingServices"
                  v-model="picaService.timing_service"
                  track-by="id"
                  label="company_name"
                  :placeholder="$gettext('Select a timing service...')"
              />
            </CCol>
          </CRow>

          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Timing service event ID</translate>
              </label>
              <div class="font-weight-bold">
                <translate>Event ID in the agency platform</translate>
              </div>
            </CCol>

            <CCol sm="7" lg="4">
              <CInput v-model="picaService.timing_service_event_id"/>
            </CCol>
          </CRow>
        </CContainer>


      </CCardFooter>

      <CCardFooter class="px-0 pt-0">
        <CContainer fluid class="grid-stripped">
          <CRow>
            <CCol>
              <h6>
                <translate>Prices & currencies</translate>
              </h6>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="5" lg="3">
              <label>
                <translate>Free photos</translate>
              </label>
              <div class="font-weight-bold" v-translate>Deliver photos to all users for free</div>
            </CCol>
            <CCol sm="7" lg="4">
              <CSwitch v-if="isFieldEditable('picaService.is_promo')"
                       class="mx-1" color="primary"
                       variant="3d" size="sm" :checked.sync="picaService.is_promo"/>
            </CCol>
          </CRow>

          <CRow v-show="picaService.is_promo === false">
            <CCol sm="5" lg="3">
              <label>
                <translate>Currency</translate>
              </label>
            </CCol>

            <CCol sm="7" lg="4" v-if="isFieldEditable('picaService.currency')">
              <multiselect
                  :options="currencies"
                  v-model="selectedCurrency"
                  track-by="code"
                  label="name"
                  placeholder="Scegli la valuta..."
                  :allow-empty="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">
                  {{ option.name }} ({{ option.code }})
                </template>
              </multiselect>
              <div class="invalid-feedback" style="display: block" v-if="isFieldValid('currency') === false">
                {{ getFieldErrors('currency') }}
              </div>
            </CCol>

            <CCol sm="7" lg="4" v-else>
              {{ picaService.currency }}
            </CCol>
          </CRow>

          <CRow v-show="picaService.is_promo === false">
            <CCol sm="5" lg="3">
              <label>
                <translate>Default price list</translate>
              </label>
            </CCol>
            <CCol sm="7" lg="4">
              <PriceListSelect :items="priceLists"
                               v-model="picaService.pricelist_static"
                               :field-errors="getFieldErrors('pricelist_static_id')"
              />
            </CCol>
          </CRow>
        </CContainer>

      </CCardFooter>
    </CCard>

    <!-- TODO: v-if change_price_list -->
    <PicacodeReleases v-if="this.isEditMode && picaService.is_promo === false"
                      :pica-service-id="picaService.id"
                      :price-lists="priceLists"
                      :codecs="codecs"
                      :show-picacode-release-id="isPicaManager"
                      :can-change="hasPerm('pica.change_picacoderelease')"
                      :can-view-codec="isFieldVisible('picaService.codec')"
    />

    <!-- TODO: componente CCard che elenca le voci del log filtrate per contesto -->

    <CCard v-if="this.isEditMode && (isFieldVisible('copyright_images') || isFieldVisible('picaService.cover'))">
      <CCardHeader>
        <h5 class="my-2">
          <translate>Sponsor band</translate>
        </h5>
      </CCardHeader>

      <CCardBody class="px-0">
        <CContainer fluid class="grid-stripped">
          <CRow v-if="isFieldVisible('copyright_images')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Portrait</translate>
              </label>
              <div class="font-weight-bold">
                <translate>For vertical photo</translate>
              </div>
            </CCol>
            <CCol sm="7" lg="4">
              <PImageUploader :pica-service-id="picaService.id"
                              field-name="copyright_image_portrait"
                              :current-image="picaService.copyright_image_portrait"
                              :validation-options="{
                                            acceptedFileTypes: 'image/png'
                                        }"
                              @upload-completed="updateImageField('copyright_image_portrait', $event)"
                              @image-removed="updateImageField('copyright_image_portrait', $event)"
              />
            </CCol>
          </CRow>

          <CRow v-if="isFieldVisible('copyright_images')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Landscape</translate>
              </label>
              <div class="font-weight-bold">
                <translate>For horizontal photo</translate>
              </div>
            </CCol>
            <CCol sm="7" lg="4">
              <PImageUploader :pica-service-id="picaService.id"
                              field-name="copyright_image"
                              :current-image="picaService.copyright_image"
                              :validation-options="{
                                            acceptedFileTypes: 'image/png'
                                        }"
                              @upload-completed="updateImageField('copyright_image', $event)"
                              @image-removed="updateImageField('copyright_image', $event)"
              />
            </CCol>
          </CRow>

          <CRow v-if="isFieldVisible('picaService.cover')">
            <CCol sm="5" lg="3">
              <label>
                <translate>Cover</translate>
              </label>
              <div class="font-weight-bold">
                <translate>For event logo</translate>
              </div>
            </CCol>
            <CCol sm="7" lg="4">
              <PImageUploader :pica-service-id="picaService.id"
                              field-name="cover"
                              :current-image="picaService.cover"
                              :validation-options="{
                                            acceptedFileTypes: 'image/png,image/jpeg'
                                        }"
                              :compressor-options="{
                                            maxWidth: 1024,
                                            maxHeight: 1024
                                        }"
                              @upload-completed="updateImageField('cover', $event)"
                              @image-removed="updateImageField('cover', $event)"
              />
            </CCol>
          </CRow>
        </CContainer>

      </CCardBody>
    </CCard>
  </CContainer>

</template>


<script>
import Constants from "../../constants"
import CodecService from "../../services/codec.service"
import PriceListService from "../../services/price_list.service"
import PhotosiPrintsService from "../../services/photosi_prints.service"
import PicaServiceService from "../../services/pica_service.service"
import PhotoServiceService from "../../services/photo_service.service"
import TimingServiceService from "../../services/timing_service.service"
import PrintingServiceService from "../../services/printing_service.service"
import Multiselect from 'vue-multiselect'
import PriceListSelect from "../../components/PriceListSelect"
import EventOwnerService from "../../services/event_owner.service"
import PMultiselectList from "../../components/PMultiselectList"
import PButton from "../../components/PButton"
import PImageUploader from "../../components/PImageUploader"
import CSwitchConfirm from "../../components/CSwitchConfirm"
import {get} from "@/utils/objects"
import PicacodeReleases from "@/components/PicacodeReleases"
import ButtonCompleteTag from "@/components/ButtonCompleteTag"
import PSelect from "@/components/PSelect"

import {getParameterByName} from '@/utils/routes'
import {mapGetters} from "vuex";

export default {
  name: "EventSetup",

  components: {
    ButtonCompleteTag,
    PicacodeReleases,
    PriceListSelect,
    Multiselect,
    CSwitchConfirm,
    PSelect,
    PMultiselectList,
    PImageUploader,
    PButton,
    // GoogleMap
    // PInputTextMultilang
  },

  data() {
    return {
      loading: false,
      // loadingTag: false,

      isEditMode: false,
      // showModalConfirmTagCompleted: false,
      validationErrors: null,
      picaService: null,

      isActive: false,
      isPublished: false,

      eventCategories: [],
      codecs: [],

      recognitionClasses: [
        {code: 'none', name: this.$gettext('none')},
        {code: 'face', name: this.$gettext('face')},
        {code: 'person', name: this.$gettext('person')},
        {code: 'bike', name: this.$gettext('bike')},
        {code: 'horse', name: this.$gettext('horse')},
      ],
      selectedRecognitionClass: {code: 'face', name: this.$gettext('face')},
      maxFacesCount: [
        2, 3, 4, 5, 6, 7, 8, 9, 10
      ],
      previous_max_faces_count: 5,
      currencies: [],
      selectedCurrency: {code: 'EUR', name: 'Euro'},

      photoServices: [],
      photoServicePhotographers: [],
      photoServicePhotographerManagers: [],

      timingServices: [],
      printingServices: [],
      priceLists: [],
      printPriceLists: [],

      eventOwners: [],
      eventOwnersNextPage: null,
      eventOwnersLoading: false,

      fieldsVisibleByGroup: {
        [Constants.USER_GROUP_PICA_MANAGERS]: [
          'picaService.event.is_active',
          'picaService.event.category',
          'picaService.codec',
          'picaService.recognition_class',
          'picaService.face_match_min_confidence',
          'picaService.allows_upload_from_mobile',
          'picaService.allows_upload_video',
          'picaService.allows_upload_from_video_extraction',
          'picaService.is_magictag_enabled',
          'picaService.checkin_max_faces_count',
          'picaService.checkin_ask_face_validity',
          'picaService.checkin_anonymize_enabled',
          'picaService.has_media_original',
          'picaService.print_service_enabled_v2',
          'picaService.printing_service',
          'picaService.event_owner',
          'picaService.timing_service',
          'event_address',
          'copyright_images',
          'picaService.cover'
        ],

        [Constants.USER_GROUP_PHOTOGRAPHERS_MANAGERS]: [
          'picaService.is_tagging_completed',
          'picaService.event.photo_service',
          'picaService.event.start_at',
          'picaService.event.end_at',
          'picaService.hd_available_from',
        ]
      },

      fieldsEditableByGroup: {
        [Constants.USER_GROUP_PHOTOGRAPHERS_MANAGERS]: [
          'picaService.is_tagging_completed',
          'picaService.is_promo',
          'picaService.event.start_at',
          'picaService.event.end_at',
        ]
      },
    }
  },

  mounted() {
    this.isEditMode = this.$store.getters['event/isInEvent']

    if (this.isEditMode) {
      // Commented as the router automatically refresh the PicaService object in store whenever there is a route change
      // See "event/fetchPicaService" in router/index.js
      // this.refreshPicaServiceInStore()
      //     .then(() => {
      //         this.fetchLookups()
      //     })
      this.initPicaServiceFromStore()
    } else {
      this.picaService = {
        'event': {
          'name': null,
          'start_at': null,
          'end_at': null,
        },
        'is_promo': false,
        'currency': this.selectedCurrency.code,
        'codec': null,
        'recognition_class': this.selectedRecognitionClass.code,
        'face_match_min_confidence': 0.97,
        'photographers': [],
        'print_pricelist': null,
        'event_owner': null,
        'has_media_original': true,
        'hd_available_from': null,
        'is_magictag_enabled': false,
        'checkin_max_faces_count': 1,
        'checkin_ask_face_validity_end': false,
        'checkin_ask_face_validity_start': false,
        'checkin_anonymize_enabled': false,
      }
      // PicaServiceService.getPicaServiceEmptyObject()
      //     .then((response) => {
      //         this.picaService = response.data
      //     })
    }

    // TODO: fare il fetch dei soli campi necessari in funzione del ruolo dell'utente
    this.fetchLookups()
  },

  watch: {
    selectedCurrency: function (newVal) {
      this.picaService.currency = newVal.code
    },

    selectedRecognitionClass: function (newVal) {
      this.picaService.recognition_class = newVal.code
    },

    'picaService.has_media_original': function (newVal) {
      if (newVal === false) {
        // reset photosi prints configuration
        this.picaService.print_service_enabled_v2 = false
        this.picaService.print_pricelist = null
        this.picaService.hd_available_from = null
      }
    },

    'picaService.checkin_ask_face_validity_end': function (newVal) {
      if (newVal === false) {
        // reset checkin_ask_face_validity_start value
        this.picaService.checkin_ask_face_validity_start = false
      }
    },

    'picaService.photo_service.id': function (newPhotoServiceId, oldPhotoService) {
      // console.log('picaService.photo_service.id', newPhotoServiceId, oldPhotoService)

      this.fetchPhotoServiceUsers(newPhotoServiceId)
          .then(() => {
            if (oldPhotoService !== undefined) {
              this.picaService.photographers = []
              this.$set(this.picaService, 'photographer_manager', {})
            }
          })
    },

    /*'picaService.print_service_enabled_v2': function (newVal, oldVal) {
        console.log('picaService.print_service_enabled_v2 CHANGED', newVal, oldVal)

        if (newVal === true) {
            if (this.picaService.print_pricelist === null) {
                console.log('setto il listino prezzi se ancora non lo è')
            }
        }
    }*/
  },

  computed: {
    ...mapGetters('login', ['user', 'isPicaManager']),
    userGroups() {
      return this.user.groups
    },
    isSelectedFaceRecognitionCodec() {
      if (this.picaService.codec) {
        return this.picaService.codec.is_face_recognition === true
      }
      return false
    },

    isCheckinMultipleFacesEnabled() {
      return this.picaService.checkin_max_faces_count > 1
    },

    displayFaceMatchMinConfidence() {
      return parseFloat(this.picaService.face_match_min_confidence).toFixed(2)
    },

    mapMarkers() {
      if (!this.picaService.event.lat && !this.picaService.event.lng) {
        return null
      }

      return [
        {
          position: {
            lat: this.picaService.event.lat,
            lng: this.picaService.event.lng
          },
          formatted_address: this.picaService.event.place_address
        }
      ]
    }
  },

  methods: {
    isFieldVisible(fieldName) {
      /*
      se l'utente non appartiene a nessuno dei gruppi specificati, mostro il campo;
      se vi appartiene ma il fieldName non è tra quelli del gruppo, non lo mostro
      */
      let isVisible = true
      for (const [group, visibleFields] of Object.entries(this.fieldsVisibleByGroup)) {
        if (this.userGroups.includes(group)) {
          isVisible = false
          if (visibleFields.includes(fieldName))
            return true
        }
      }
      return isVisible
    },

    isFieldEditable(fieldName) {
      let isEditable = true
      for (const [group, editableFields] of Object.entries(this.fieldsEditableByGroup)) {
        if (this.userGroups.includes(group)) {
          isEditable = false
          if (editableFields.includes(fieldName))
            return true
        }
      }
      return isEditable
    },

    updatePlace(place) {
      // console.log('place', place)
      const googleFields = [
        'locality', 'route', 'street_number', 'postal_code', 'administrative_area_level_3',
        'administrative_area_level_2', 'administrative_area_level_2_short_name',
        'administrative_area_level_1', 'administrative_area_level_1_short_name',
        'country', 'country_short_name'
      ]

      googleFields.forEach((gField) => {
        let f = gField
        const isShortNameField = gField.endsWith('_short_name')
        if (isShortNameField) {
          f = gField.replace('_short_name', '')
        }
        const addressComponent = place.address_components.find((c) => c.types['0'] === f)
        if (addressComponent) {
          this.picaService.event[gField] = isShortNameField ? addressComponent.short_name : addressComponent.long_name
        } else {
          this.picaService.event[gField] = ""
        }
      })

      this.picaService.event.lat = place.geometry.location.lat()
      this.picaService.event.lng = place.geometry.location.lng()
    },

    handleCodecChange(selectedCodec) {
      // console.log('handleCodecChange', selectedCodec)
      this.picaService.codec = selectedCodec

      if (selectedCodec.is_face_recognition) {
        // setup fields
        this.picaService.allows_upload_video = false
        this.picaService.allows_upload_from_video_extraction = false
      } else {
        this.picaService.allows_upload_from_mobile = false
      }
    },

    toggleCheckinMultipleFacesEnabled() {
      if (this.picaService.checkin_max_faces_count > 1) {
        this.previous_max_faces_count = this.picaService.checkin_max_faces_count ? this.picaService.checkin_max_faces_count : 5
      }
      this.picaService.checkin_max_faces_count = this.picaService.checkin_max_faces_count === 1 ? this.previous_max_faces_count : 1
    },

    initPicaServiceFromStore() {
      const originalPicaService = this.$store.state.event.picaService
      this.picaService = JSON.parse(JSON.stringify(originalPicaService))
      this.isActive = this.picaService.event.is_active
      this.isPublished = this.picaService.event.published

      // this.isTaggingCompleted = this.picaService.event.is_tagging_completed

      // this.picaService.event.start_at = moment(this.picaService.event.start_at).local().format('YYYY-MM-DDTHH:mm:ssZ')
      // this.picaService.event.end_at = moment(this.picaService.event.end_at).local().format('YYYY-MM-DDTHH:mm:ssZ')
      // if (this.picaService.hd_available_from !== null) {
      //     this.picaService.hd_available_from = moment(this.picaService.hd_available_from).local().format('YYYY-MM-DDTHH:mm:ssZ')
      // }
    },

    refreshPicaServiceInStore() {
      return this.$store.dispatch('event/fetchPicaService')
          .then(() => {
            this.initPicaServiceFromStore()
          })
    },

    fetchEventOwners(page = 1, query = '') {
      this.eventOwnersLoading = true

      if (this.hasPerm('pica.view_eventowner')) {
        let params = {
          page: page
        }
        if (query !== '')
          params['search'] = query

        EventOwnerService.getEventOwners(params)
            .then((response) => {
              this.eventOwners = this.eventOwners.concat(response.data.results)
              if (response.data.next !== null) {
                this.eventOwnersNextPage = parseInt(getParameterByName('page', response.data.next), null);
              } else {
                this.eventOwnersNextPage = null
              }

              this.eventOwnersLoading = false
            })
      }
    },

    clearEventOwners() {
      this.eventOwners = []
      this.eventOwnersNextPage = null
    },

    fetchLookups() {
      PicaServiceService.getEventCategories()
          .then((response) => {
            this.eventCategories = response.data.results
          })

      let codecParams = {
        isActive: true
      }
      if (this.isEditMode) {
        codecParams.picaServiceId = this.$store.getters['event/picaServiceId']
      }
      CodecService.getCodecs(codecParams)
          .then((response) => {
            this.codecs = response.data
          })

      if (this.picaService.recognition_class) {
        this.selectedRecognitionClass = this.recognitionClasses.find(i => i.code === this.picaService.recognition_class)
      }
      PriceListService.getCurrencies()
          .then((response) => {
            this.currencies = response.data
            if (this.picaService.currency) {
              this.selectedCurrency = this.currencies.find(i => i.code === this.picaService.currency)
            }
          })

      if (this.hasPerm('pica.view_photoservice')) {
        PhotoServiceService.getPhotoServices({is_active: true, page_size: 1000})
            .then((response) => {
              this.photoServices = response.data.results
            })
      }

      if (this.hasPerm('pica.view_timingservice')) {
        TimingServiceService.getTimingServices()
            .then((response) => {
              this.timingServices = response.data.results
            })
      }

      if (this.hasPerm('pica.view_printingservice')) {
        PrintingServiceService.getPrintingServices()
            .then((response) => {
              this.printingServices = response.data.results
            })
      }

      if (this.hasPerm('pica.view_pricelist')) {
        PriceListService.getPriceLists(this.picaServiceId || null)
            .then((response) => {
              this.priceLists = response.data.results
            })
      }

      if (this.hasPerm('photosi_prints.view_pricelist')) {
        PhotosiPrintsService.getPriceLists()
            .then((response) => {
              this.printPriceLists = response.data.results
              // TODO: set in picaService.print_pricelist the default price list
              if (this.picaService.print_pricelist === null) {
                this.picaService.print_pricelist = this.printPriceLists.find((pl) => pl.default === true)
              }
            })
      }

      this.fetchEventOwners()
    },

    reachedEndOfList(reached) {
      if (reached) {
        //this.fetchNextPage();
        console.log(reached)
      }
    },

    fetchPhotoServiceUsers(photoServiceId) {
      return PhotoServiceService.getPhotoServiceUsers(photoServiceId)
          .then((response) => {
            this.photoServicePhotographers = response.data.photographers
            this.photoServicePhotographerManagers = response.data.photographer_managers
          })
    },

    checkDateTimeValue(newVal) {
      if (newVal === null) {
        // TODO: ripristinare solo se sono in edit mode
        this.picaService.event.start_at = this.$store.state.event.picaService.event.start_at
      }
      console.log('checkDateTimeValue', newVal)
    },

    isFieldValid(fieldName) {
      if (this.validationErrors === null)
        return null

      const fieldValue = get(this.validationErrors, fieldName)
      if (fieldValue)
        return false
      return null
    },

    getFieldErrors: function (fieldName) {
      if (this.isFieldValid(fieldName) === false) {
        const fieldValue = get(this.validationErrors, fieldName)
        return fieldValue.join(' ')
      }
      return ''
    },

    getPreparedPicaService() {
      const picaService = JSON.parse(JSON.stringify(this.picaService))

      // if (picaService['event']['name'] === null || picaService['event']['name'] === '')
      //     delete picaService['event']['name']

      if (picaService['event']['start_at'] === null)
        delete picaService['event']['start_at']
      else
        picaService['event']['start_at'] = new Date(picaService['event']['start_at']).toISOString()

      if (picaService['event']['end_at'] === null)
        delete picaService['event']['end_at']
      else
        picaService['event']['end_at'] = new Date(picaService['event']['end_at']).toISOString()

      if (picaService['hd_available_from'] === null)
        delete picaService['hd_available_from']
      else
        picaService['hd_available_from'] = new Date(picaService['hd_available_from']).toISOString()

      if ('category' in picaService['event']) {
        picaService['event']['category_id'] = picaService['event']['category']['id']
        delete picaService['event']['category']
      }

      if ('codec' in picaService) {
        if (picaService['codec'] !== null)
          picaService['codec_id'] = picaService['codec']['id']
        delete picaService['codec']
      }

      if ('photo_service' in picaService) {
        picaService['photo_service_id'] = picaService['photo_service'] === null ? null : picaService['photo_service']['id']
        delete picaService['photo_service']
      }

      if ('photographer_manager' in picaService) {
        picaService['photographer_manager_id'] = picaService['photographer_manager'] === null ? null : picaService['photographer_manager']['id']
        delete picaService['photographer_manager']
      }

      if ('photographers' in picaService) {
        picaService['photographers_ids'] = picaService['photographers'].map((user) => user.id)
        delete picaService['photographers']
      }

      if ('printing_service' in picaService) {
        picaService['printing_service_id'] = picaService['printing_service'] === null ? null : picaService['printing_service']['id']
        delete picaService['printing_service']
      }

      if ('timing_service' in picaService) {
        picaService['timing_service_id'] = picaService['timing_service'] === null ? null : picaService['timing_service']['id']
        delete picaService['timing_service']
      }

      if ('pricelist_static' in picaService) {
        picaService['pricelist_static_id'] = picaService['pricelist_static'] === null ? null : picaService['pricelist_static']['id']
        delete picaService['pricelist_static']
      }

      if ('print_pricelist' in picaService) {
        picaService['print_pricelist_id'] = picaService['print_pricelist'] === null ? null : picaService['print_pricelist']['id']
        delete picaService['print_pricelist']
      }

      if ('event_owner' in picaService) {
        picaService['event_owner_id'] = picaService['event_owner'] === null ? null : picaService['event_owner']['id']
        delete picaService['event_owner']
      }

      return picaService
    },

    handleCoverChanged(event) {
      this.fileTypeNotAcceptedError = false
      this.selectedFile = event.target.files[0]

      if (this.acceptedFileTypesArray !== null && !this.acceptedFileTypesArray.includes(this.selectedFile.type)) {
        this.fileTypeNotAcceptedError = true
        this.clearSelectedFile()
        return false
      }

      this.selectedUrl = URL.createObjectURL(this.selectedFile)
    },

    save() {
      this.loading = true
      this.validationErrors = null
      this.showAlertSaveSuccess = false

      const preparedPicaService = this.getPreparedPicaService()

      if (this.isEditMode) {
        if (JSON.stringify(this.picaService) !== JSON.stringify(this.$store.state.event.picaService)) {
          console.info(this.picaService)
          // console.info(this.$store.state.event.picaService)
          PicaServiceService.updatePicaService(this.$store.getters['event/picaServiceId'], preparedPicaService)
              .then((response) => {
                this.loading = false
                console.log('save() success!', response)
                this.$store.commit('event/SET_PICASERVICE', {picaService: response.data})
                this.notifyMessage({
                  text: this.$gettext('Event updated successfully'),  // 'Evento aggiornato correttamente',
                  messageType: 'success'
                })
                // this.showAlertSaveSuccess = true
              })
              .catch((err) => {
                this.loading = false
                console.log('save() error', err.response.data)
                this.validationErrors = err.response.data
                this.notifyMessage({
                  text: this.$gettext('An error occurred while saving: check errors below'), // 'Attenzione: errore nella compilazione dei campi',
                  messageType: 'warning'
                })
              })
        } else {
          this.loading = false
          this.notifyMessage({
            text: this.$gettext('No changes detected'), // 'Attenzione: errore nella compilazione dei campi',
            messageType: 'warning'
          })
        }

      } else {
        console.log('NEW')
        PicaServiceService.createPicaService(preparedPicaService)
            .then((response) => {
              this.loading = false
              console.log('save() success!', response)
              // this.$store.commit('event/SET_PICASERVICE', response.data)
              // TODO: redirect a route event setup
              this.notifyMessage({text: 'Evento creato correttamente', messageType: 'success'})
              this.$router.push({name: 'eventSetup', params: {picaServiceId: response.data.id}})
            })
            .catch((err) => {
              this.loading = false
              console.log('save() error', err.response.data)
              this.validationErrors = err.response.data
              this.notifyMessage({
                text: 'Attenzione: errore nella compilazione dei campi',
                messageType: 'warning'
              })
            })
      }
    },

    saveActivationStatus(e) {
      console.log('saveActivationStatus', e)
      this.isActive = e
      PicaServiceService.updatePicaService(this.$store.getters['event/picaServiceId'], {
        event: {'is_active': this.isActive}
      })
          .then((response) => {
            console.log('save() success!', response)
            this.notifyMessage({text: e ? 'Evento attivato' : 'Evento disattivato', messageType: 'success'})
            // this.showAlertSaveSuccess = true
            this.refreshPicaServiceInStore()
          })
          .catch((err) => {
            console.log('saveActivationStatus() error', err.response.data)
          })
    },

    savePublicationStatus(e) {
      console.log('savePublicationStatus', e)
      this.isPublished = e
      PicaServiceService.updatePicaService(this.$store.getters['event/picaServiceId'], {
        event: {'published': this.isPublished}
      })
          .then((response) => {
            console.log('save() success!', response)
            this.notifyMessage({
              text: e ? 'Evento pubblicato' : 'Evento non pubblicato',
              messageType: 'success'
            })
            this.refreshPicaServiceInStore()
          })
          .catch((err) => {
            console.log('savePublicationStatus() error', err.response.data)
          })
    },

    // setTagAsCompleted() {
    //     this.loadingTag = true
    //     console.log('setTagAsCompleted')
    //     this.showModalConfirmTagCompleted = false
    //     PicaServiceService.updateTagStatus(this.$store.getters['event/picaServiceId'], true)
    //         .then(() => {
    //             this.notifyMessage({text: 'Tag completato', messageType: 'success'})
    //             this.refreshPicaServiceInStore()
    //                 .then(() => this.loadingTag = false)
    //         })
    //         .catch((err) => {
    //             this.loadingTag = false
    //             console.log('savePublicationStatus() error', err.response.data)
    //         })
    // },

    updateImageField(fieldName, e) {
      console.log('updateImageField', fieldName, e)
      this.picaService[fieldName] = e
    }
  }
}
</script>
